import { WorkNavigationContext } from "../../context/WorkNavigationContext";
import { projectAdminNavigation, ADMIN_FRIENDLY_LABELS } from "../../lib/simpleNavigation";
import React, { useState, useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, UserPlus, UserSquare2, Contact, Globe, Inbox, KanbanSquare, MessageSquare, CheckCircle2,
  ReceiptText, Boxes, BarChart3, Sparkles, Star, Package, CreditCard, PlusCircle, FolderOpen, CalendarCheck,
  LogOut, Menu, X, ShieldCheck, ScrollText, Settings as SettingsIcon, ShieldAlert, DatabaseBackup, ChevronDown, Mail, Lightbulb, LayoutTemplate, Factory, Eye, Cpu,
} from "lucide-react";

const QA_PATHS = ["/admin/qa", "/admin/system", "/admin/special"];
import { useAuth } from "@/context/AuthContext";
import { useI18n, LANGS } from "@/context/I18nContext";
import { Avatar } from "@/components/common";
import EnvBadge from "@/components/EnvBadge";
import AdminNotificationBell from "@/components/AdminNotificationBell";
import api from "@/lib/api";
import { emailStatusKey } from "@/lib/emailStatus";
import { canCreateClient, isReadOnlyStaff } from "../../lib/clientPermissions";

function EmailStatusLight({ canSee }) {
  const { t } = useI18n();
  const nav = useNavigate();
  const [report, setReport] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/admin/email-status").then((r) => alive && setReport(r.data || { status: "error" })).catch(() => alive && setReport({ status: "error" }));
    return () => { alive = false; };
  }, []);
  if (!canSee("settings")) return null;
  const status = emailStatusKey(report);
  const meta = {
    enabled: { dot: "bg-[#A3E635]", ring: "bg-[#A3E635]", title: t("Brevo verbunden · allgemeiner Versand freigegeben") },
    disabled: { dot: "bg-gray-500", ring: "bg-gray-500", title: t("Brevo verbunden · allgemeiner Versand deaktiviert") },
    unconfirmed: { dot: "bg-amber-400", ring: "bg-amber-400", title: t("Brevo verbunden · Versandfreigabe unbekannt") },
    ip_blocked: { dot: "bg-amber-400", ring: "bg-amber-400", title: t("E-Mail-Versand blockiert · IP nicht freigegeben") },
    unauthorized: { dot: "bg-red-500", ring: "bg-red-500", title: t("E-Mail-Versand nicht autorisiert") },
    not_configured: { dot: "bg-gray-500", ring: "bg-gray-500", title: t("E-Mail-Versand nicht konfiguriert") },
    error: { dot: "bg-red-500", ring: "bg-red-500", title: t("E-Mail-Status unbekannt") },
  };
  const m = meta[status] || { dot: "bg-gray-600 animate-pulse", ring: "bg-gray-600", title: t("E-Mail-Status wird geprüft …") };
  return (
    <button onClick={() => nav("/admin/settings")} data-testid="email-status-light" title={m.title} aria-label={m.title}
      className="flex items-center gap-1.5 px-2 py-1 rounded-lg border border-white/10 bg-white/[0.03] hover:bg-white/[0.07] transition-colors">
      <Mail className="w-3.5 h-3.5 text-gray-400" />
      <span className="relative flex w-2 h-2">
        {status === "enabled" && <span className={`absolute inline-flex h-full w-full rounded-full ${m.ring} opacity-60 animate-ping`} />}
        <span className={`relative inline-flex rounded-full w-2 h-2 ${m.dot}`} />
      </span>
    </button>
  );
}

// Editor-Rolle: eingeschränkte Teilmenge (nur Tagesarbeit). Keine gefährlichen System-/Finanzbereiche.
const EDITOR_SECTIONS = new Set(["dashboard", "clients", "tasks", "leads", "support", "websites", "design-library", "reports"]);

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [open, setOpen] = useState(false);
  const [showAllSections, setShowAllSections] = useState(true);
  const [counts, setCounts] = useState({});
  const [wpOn, setWpOn] = useState(false);
  useEffect(() => {
    if (user?.role !== "super_admin") return;
    api.get("/admin/website-production/status").then(() => setWpOn(true)).catch(() => setWpOn(false));
  }, [user?.role]);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem("kewag_admin_nav") || "{}"); } catch { return {}; }
  });
  const nav = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const load = () => api.get("/admin/today").then((r) => setCounts(r.data?.counts || {})).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [location.pathname]);

  const qaSub = [
    { to: "/admin/qa", focus: null, label: t("Übersicht"), key: "overview" },
    { to: "/admin/qa?focus=login", focus: "login", label: t("Login & Zugriff"), key: "login" },
    { to: "/admin/qa?focus=roles", focus: "roles", label: t("Rollen & Rechte"), key: "roles" },
    { to: "/admin/qa?focus=tenants", focus: "tenants", label: t("Kundenkonten & Mandanten"), key: "tenants" },
    { to: "/admin/qa?focus=backup", focus: "backup", label: t("Backup & Wiederherstellung"), key: "backup" },
    { to: "/admin/qa?focus=audit", focus: "audit", label: t("Audit & Nachvollziehbarkeit"), key: "audit" },
    { to: "/admin/system", focus: null, label: t("System & Daten"), key: "system" },
    { to: "/admin/special", focus: null, label: t("Spezialprüfungen"), key: "special" },
  ];

  const groups = [
    {
      id: "uebersicht", label: t("grpUebersicht"), items: [
        { id: "dashboard", to: "/admin", icon: LayoutDashboard, label: t("dashboard"), section: "dashboard" },
        { id: "today", to: "/admin/today", icon: CalendarCheck, label: t("heuteWichtig"), section: "dashboard" },
        { id: "clients-overview", to: "/admin/clients", icon: Users, label: t("kundenuebersicht"), section: "clients" },
      ],
    },
    {
      id: "kunden", label: t("grpKunden"), items: [
        { id: "clients", to: "/admin/clients", icon: Users, label: t("alleKunden"), section: "clients" },
        { id: "cockpit", to: "/admin/cockpit", icon: UserSquare2, label: t("kundenCockpit"), section: "clients" },
        { id: "new-client", to: "/admin/clients/new", icon: UserPlus, label: t("newClient"), section: "new-client" },
        { id: "contacts", to: "/admin/contacts", icon: Contact, label: t("kontakte"), section: "clients" },
      ],
    },
    {
      id: "arbeit", label: t("grpArbeit"), items: [
        { id: "tasks", to: "/admin/tasks", icon: KanbanSquare, label: t("tasks"), section: "tasks", badge: "tasks" },
        { id: "leads", to: "/admin/leads", icon: Inbox, label: t("leads"), section: "leads", badge: "leads" },
        { id: "support", to: "/admin/support", icon: MessageSquare, label: t("support"), section: "support", badge: "support" },
        { id: "approvals", to: "/admin/approvals", icon: CheckCircle2, label: t("freigaben"), section: "tasks", badge: "freigaben" },
        { id: "ai-requests", to: "/admin/ai-requests", icon: Lightbulb, label: t("aiWishes"), section: "support", badge: "ai_requests" },
      ],
    },
    {
      id: "website", label: t("grpWebsite"), items: [
        { id: "websites", to: "/admin/websites", icon: Globe, label: t("websites"), section: "websites" },
        { id: "website-production", to: "/admin/website-production", icon: Factory, label: t("Website-Produktion"), section: "website-production", wp: true },
        { id: "pc-production", to: "/admin/pc-production", icon: Cpu, label: t("PC-Produktion"), section: "pc-production" },
        { id: "files", to: "/admin/files", icon: FolderOpen, label: t("dateienFreigaben"), section: "websites", badge: "dateien", badgeTone: "warn" },
        { id: "performance", to: "/admin/performance", icon: BarChart3, label: t("besuchePerformance"), section: "websites" },
        { id: "backup", to: "/admin/backup", icon: DatabaseBackup, label: t("backupRestore"), section: "backup" },
        { id: "qa", to: "/admin/qa", icon: ShieldCheck, label: t("securityQa"), section: "qa", sub: qaSub, badge: "qa", badgeTone: "warn" },
      ],
    },
    {
      id: "wachstum", label: t("grpWachstum"), items: [
        { id: "ideas", to: "/admin/ideas", icon: Sparkles, label: t("ideenVerbesserungen"), section: "design-library" },
        { id: "design-library", to: "/admin/design-library", icon: LayoutTemplate, label: t("designBibIntern"), section: "design-library" },
        { id: "modules", to: "/admin/modules", icon: Boxes, label: t("modules"), section: "modules" },
        { id: "reviews", to: "/admin/reviews", icon: Star, label: t("bewertungen"), section: "reports" },
        { id: "reports", to: "/admin/reports", icon: BarChart3, label: t("reports"), section: "reports" },
      ],
    },
    {
      id: "finanzen", label: t("grpFinanzen"), items: [
        { id: "invoices", to: "/admin/invoices", icon: ReceiptText, label: t("invoices"), section: "invoices", badge: "invoices", badgeTone: "warn" },
        { id: "subscriptions", to: "/admin/subscriptions", icon: Package, label: t("abosPakete"), section: "invoices" },
        { id: "payments", to: "/admin/payments", icon: CreditCard, label: t("zahlungen"), section: "invoices" },
        { id: "extras", to: "/admin/extras", icon: PlusCircle, label: t("zusatzleistungen"), section: "invoices" },
      ],
    },
    {
      id: "system", label: t("grpSystem"), items: [
        { id: "security", to: "/admin/security", icon: ShieldAlert, label: t("usersSecurity"), section: "security" },
        { id: "audit-log", to: "/admin/audit-log", icon: ScrollText, label: t("auditLog"), section: "audit-log" },
        { id: "settings", to: "/admin/settings", icon: SettingsIcon, label: t("settings"), section: "settings" },
      ],
    },
  ];

  // RBAC (UI): Super Admin sieht alles. Editor = feste Teilmenge. Team-Mitglied = admin_sections.
  const isEditor = user?.role === "team_member" && (user?.team_role === "editor" || user?.editor === true);
  const canSee = (section) => {
    if (user?.role === "super_admin") return true;
    if (isEditor) return EDITOR_SECTIONS.has(section);
    return Array.isArray(user?.admin_sections) && user.admin_sections.includes(section);
  };
  const visibleGroups = groups
    .map((g) => ({ ...g, items: g.items.filter((it) => (!it.wp || wpOn) && canSee(it.section) && (it.id !== "new-client" || canCreateClient(user))) }))
    .filter((g) => g.items.length > 0);

  const focusParam = new URLSearchParams(location.search).get("focus");
  const qaAreaActive = QA_PATHS.includes(location.pathname);
  const isSubActive = (s) => {
    if (s.to === "/admin/system") return location.pathname === "/admin/system";
    if (s.to === "/admin/special") return location.pathname === "/admin/special";
    if (location.pathname !== "/admin/qa") return false;
    return s.focus ? focusParam === s.focus : !focusParam;
  };

  const isItemActive = (it) => {
    const p = location.pathname;
    if (it.sub) return qaAreaActive;
    const base = it.to.split("?")[0];
    if (base === "/admin") return p === "/admin";
    if (base === "/admin/clients/new") return p === "/admin/clients/new";
    if (base === "/admin/clients") return p === "/admin/clients" || (p.startsWith("/admin/clients/") && !p.startsWith("/admin/clients/new"));
    return p === base || p.startsWith(base + "/");
  };
  const isGroupActive = (g) => g.items.some(isItemActive);
  const isGroupOpen = (g) => isGroupActive(g) || !collapsed[g.id];
  const toggleGroup = (id) => setCollapsed((c) => {
    const n = { ...c, [id]: !c[id] };
    localStorage.setItem("kewag_admin_nav", JSON.stringify(n));
    return n;
  });

  const badgeClass = (active, tone) => {
    if (active) return "bg-[#071005] text-[#A3E635]";
    if (tone === "warn") return "bg-amber-500/90 text-black";
    if (tone === "danger") return "bg-red-500 text-white";
    return "bg-[#A3E635] text-black";
  };

  const goItem = (it) => { nav(it.to); setOpen(false); };

  const friendlyGroups = visibleGroups.map(g => ({ ...g, items: g.items.map(it => ({ ...it, label: ADMIN_FRIENDLY_LABELS[it.id] ? t(ADMIN_FRIENDLY_LABELS[it.id]) : it.label })) }));
  const simpleNav = projectAdminNavigation(friendlyGroups, { showAll: showAllSections, isActive: isItemActive, dailyLabel: t("Tägliche Arbeit"), currentLabel: t("Geöffneter Bereich") });

  const NavItems = () => (
    <nav className="space-y-2.5" data-testid="admin-sidebar-nav">
      {simpleNav.groups.map((g) => {
        const openGroup = isGroupOpen(g);
        return (
          <div key={g.id}>
            <button onClick={() => toggleGroup(g.id)} data-testid={`nav-group-${g.id}`} aria-expanded={openGroup}
              className="w-full flex items-center justify-between px-3 py-1 group">
              <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-gray-400 group-hover:text-gray-400 transition-colors">{g.label}</span>
              <ChevronDown className={`w-3 h-3 text-gray-600 transition-transform duration-200 ${openGroup ? "rotate-0" : "-rotate-90"}`} />
            </button>
            {openGroup && (
              <div className="space-y-1 mt-1">
                {g.items.map((it) => {
                  const active = isItemActive(it);
                  const badge = it.badge ? Number(counts[it.badge] || 0) : 0;
                  return (
                    <div key={it.id}>
                      <button onClick={() => goItem(it)} title={it.label}
                        data-testid={`nav-${it.id}`} aria-current={active ? "page" : undefined}
                        className={`w-full flex items-center gap-2.5 px-3 py-2 min-h-[44px] rounded-xl text-[13px] leading-tight transition-all duration-200 ${
                          active ? "text-[#071005] font-semibold shadow-[0_3px_12px_rgba(163,230,53,0.2)]"
                                 : "text-gray-300 font-medium hover:text-white hover:bg-[#A3E635]/[0.08]"}`}
                        style={active ? { background: "linear-gradient(135deg,#a3e635,#84cc16)" } : {}}>
                        <it.icon className="w-[17px] h-[17px] shrink-0" />
                        <span className="flex-1 text-left truncate">{it.label}</span>
                        {it.sub && <ChevronDown className={`w-3.5 h-3.5 shrink-0 transition-transform ${active ? "rotate-180" : ""}`} />}
                        {badge > 0 && (
                          <span data-testid={`nav-badge-${it.id}`}
                            className={`shrink-0 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${badgeClass(active, it.badgeTone)}`}>
                            {badge > 99 ? "99+" : badge}
                          </span>
                        )}
                      </button>
                      {it.sub && qaAreaActive && (
                        <div className="mt-1 ml-[18px] pl-3 border-l border-white/10 space-y-0.5" data-testid="qa-subnav">
                          {it.sub.map((s) => {
                            const sactive = isSubActive(s);
                            return (
                              <button key={s.key} onClick={() => { nav(s.to); setOpen(false); }}
                                data-testid={`qa-subnav-${s.key}`} aria-current={sactive ? "page" : undefined}
                                className={`w-full text-left flex items-center gap-2 px-3 py-1.5 min-h-[32px] rounded-lg text-[12.5px] leading-tight transition-all duration-200 ${
                                  sactive ? "text-[#071005] font-semibold" : "text-gray-400 font-medium hover:text-white hover:bg-[#A3E635]/[0.08]"}`}
                                style={sactive ? { background: "linear-gradient(135deg,#a3e635,#84cc16)" } : {}}>
                                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${sactive ? "bg-[#071005]" : "bg-gray-600"}`} />
                                <span className="truncate">{s.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
      {simpleNav.extraCount > 0 && <button type="button" onClick={() => setShowAllSections(v => !v)} aria-expanded={showAllSections} data-testid="nav-more" className="w-full min-h-[44px] flex items-center gap-2.5 rounded-xl px-3 py-2 text-sm text-gray-300 hover:bg-white/5 focus-visible:outline focus-visible:outline-2">
        <Menu className="w-[17px] h-[17px]" aria-hidden="true"/><span className="text-left flex-1">{t(showAllSections ? "Weniger Bereiche anzeigen" : "Alle Bereiche anzeigen")}</span><span className="text-xs text-gray-500">{simpleNav.extraCount}</span>
      </button>}
    </nav>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0C] flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-[270px] border-r border-white/10 px-3 py-3 fixed inset-y-0">
        <div className="flex items-center gap-2.5 px-1.5 pt-1 pb-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#A3E635] flex items-center justify-center font-black text-black shrink-0">K</div>
          <div className="leading-none">
            <div className="font-bold tracking-tight text-[15px]">KEWAGPage</div>
            <div className="text-[9.5px] text-gray-500 uppercase tracking-[0.2em] mt-1">Control OS</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto sidebar-scroll -mr-1 pr-1"><NavItems /></div>
        <div className="pt-3 mt-1 border-t border-white/10 space-y-2">
          <div className="flex items-center gap-2.5 rounded-lg bg-white/[0.03] border border-white/10 p-2">
            <Avatar name={user?.name} size={32} src={user?.picture} />
            <div className="min-w-0 flex-1">
              <div className="text-[13px] font-semibold truncate" data-testid="nav-identity">
                {user?.name} · <span data-testid="nav-identity-role">{user?.role === "super_admin" ? t("Super Admin") : t(user?.team_role_label || "Teammitglied")}</span>
              </div>
              <div className="text-[10.5px] text-gray-500 truncate">{user?.email}</div>
            </div>
          </div>
          <button onClick={() => nav("/admin/my-security")} data-testid="nav-my-security"
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white hover:bg-white/5 transition-colors">
            <ShieldCheck className="w-4 h-4" /> {t("Meine Sicherheit")}
          </button>
          <button onClick={logout} data-testid="logout-btn"
            className="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-[13px] text-gray-400 hover:text-white hover:bg-white/5 transition-colors">
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
          <EnvBadge />
        </div>
      </aside>

      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 h-14 backdrop-blur-xl bg-[#0A0A0C]/80 border-b border-white/10">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-[#A3E635] flex items-center justify-center font-black text-black text-sm">K</div>
          <span className="font-bold">KEWAGPage</span>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="mobile-menu-btn" aria-label={t("Menü")} aria-expanded={open} className="p-2 min-h-[44px] min-w-[44px]">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-30 bg-[#0A0A0C] pt-16 px-4 overflow-y-auto" data-testid="mobile-drawer">
          <NavItems />
          <button onClick={() => { nav("/admin/my-security"); setOpen(false); }} data-testid="nav-my-security-mobile"
            className="mt-4 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-white/5">
            <ShieldCheck className="w-4 h-4" /> {t("Meine Sicherheit")}
          </button>
          <button onClick={logout} className="mt-4 w-full flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-gray-400 hover:bg-white/5">
            <LogOut className="w-4 h-4" /> {t("logout")}
          </button>
          <div className="mt-4"><EnvBadge /></div>
        </div>
      )}

      <main className="flex-1 min-w-0 lg:ml-[270px] px-4 sm:px-8 py-8 pt-20 lg:pt-8">
        <div className="flex justify-end items-center mb-2 gap-2">
          {isReadOnlyStaff(user) && (
            <span data-testid="readonly-badge" title={t("Ihre Rolle kann Daten ansehen, aber nicht verändern.")}
              className="mr-auto inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-0.5 text-[11px] font-semibold text-gray-400">
              <Eye className="w-3 h-3" /> {t("Nur Lesezugriff")}
            </span>
          )}
          <AdminNotificationBell />
          <EmailStatusLight canSee={canSee} />
          <div className="flex gap-1">
            {LANGS.map((l) => (
              <button key={l.code} onClick={() => setLang(l.code)} data-testid={`lang-btn-${l.code}`}
                className={`px-2 py-0.5 rounded text-[11px] font-bold transition-colors ${lang === l.code ? "bg-[#A3E635] text-black" : "text-gray-500 hover:text-white"}`}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
        <WorkNavigationContext.Provider value={friendlyGroups.flatMap(g => g.items)}><Outlet /></WorkNavigationContext.Provider>
      </main>
    </div>
  );
}

